import { z } from 'zod';

import i18n from '@/core/i18n';

import { TimeOffAllocationFormat } from '../types/time-off';

export const ALLOCATION_DAYS_YEARLY_MAX_VALUE = 366;
export const ALLOCATION_DAYS_MONTHLY_MAX_VALUE = 31;
export const ALLOCATION_DAYS_MIN_VALUE = 1;
export const ALLOCATION_DAYS_YEARLY_MAX_DECIMAL_PLACES = 0;
export const ALLOCATION_DAYS_MONTHLY_MAX_DECIMAL_PLACES = 2;

export const CARRYOVER_DAYS_MAX_INTEGER_DIGITS = 4;
export const CARRYOVER_DAYS_MAX_DECIMAL_PLACES = 0;
export const CARRYOVER_DAYS_MIN_VALUE = 1;

export const NEGATIVE_BALANCE_DAYS_MAX_VALUE = 366;
export const NEGATIVE_BALANCE_DAYS_MAX_INTEGER_DIGITS = 3;
export const NEGATIVE_BALANCE_DAYS_MAX_DECIMAL_PLACES = 0;
export const NEGATIVE_BALANCE_DAYS_MIN_VALUE = 1;

export const MAX_LIMIT_DAYS_MAX_VALUE = 366;
export const MAX_LIMIT_DAYS_MAX_INTEGER_DIGITS = 3;
export const MAX_LIMIT_DAYS_MAX_DECIMAL_PLACES = 0;
export const MAX_LIMIT_DAYS_MIN_VALUE = 1;

const t = (key: string, params?: Record<string, unknown>) =>
  i18n.t(`authenticated.settings:pages.timeOff.details.validation.${key}`, params);

export const timeOffTypeSetupSchema = z
  .object({
    allocationFormat: z.nativeEnum(TimeOffAllocationFormat),
    // Base type only — empty inputs are coerced to 0 by the number field, and the
    // min/max/format rules (with localized messages) are enforced in superRefine.
    allocationDays: z.number(),
    dateOfRenewal: z.object({
      month: z.number().int().min(1).max(12),
      day: z.number().int().min(1).max(31),
    }),

    allowCarryover: z.boolean(),
    carryoverDays: z
      .number()
      .int()
      .positive()
      .max(Math.pow(10, CARRYOVER_DAYS_MAX_INTEGER_DIGITS) - 1)
      .nullable(),

    allowNegativeBalance: z.boolean(),
    negativeBalanceDays: z
      .number()
      .int()
      .positive()
      .max(Math.pow(10, NEGATIVE_BALANCE_DAYS_MAX_INTEGER_DIGITS) - 1)
      .nullable(),

    hasMaxLimit: z.boolean(),
    maxLimitDays: z
      .number()
      .int()
      .positive()
      .max(Math.pow(10, MAX_LIMIT_DAYS_MAX_INTEGER_DIGITS) - 1)
      .nullable(),

    isApprovalRequired: z.boolean(),
    requireFileUpload: z.boolean(),
    allowHalfDay: z.boolean(),
    allowQuarterDay: z.boolean(),
    allowHourlyRequests: z.boolean(),
    considerMemberCapacity: z.boolean(),
    creditOnCreation: z.boolean(),
    prorateAccrual: z.boolean(),
  })
  .superRefine((data, ctx) => {
    if (data.allocationFormat === TimeOffAllocationFormat.Yearly) {
      if (
        data.allocationDays < ALLOCATION_DAYS_MIN_VALUE ||
        !Number.isInteger(data.allocationDays) ||
        data.allocationDays > ALLOCATION_DAYS_YEARLY_MAX_VALUE
      ) {
        ctx.addIssue({
          path: ['allocationDays'],
          code: 'custom',
          message: t('allocationDaysYearly', { max: ALLOCATION_DAYS_YEARLY_MAX_VALUE }),
        });
      }
    } else {
      const rounded = Math.round(data.allocationDays * 100) / 100;
      if (
        data.allocationDays <= 0 ||
        rounded !== data.allocationDays ||
        data.allocationDays > ALLOCATION_DAYS_MONTHLY_MAX_VALUE
      ) {
        ctx.addIssue({
          path: ['allocationDays'],
          code: 'custom',
          message: t('allocationDaysMonthly', { max: ALLOCATION_DAYS_MONTHLY_MAX_VALUE }),
        });
      }
    }

    if (data.allowCarryover && (data.carryoverDays == null || data.carryoverDays <= 0)) {
      ctx.addIssue({
        path: ['carryoverDays'],
        code: 'custom',
        message: t('carryoverDaysRequired'),
      });
    }

    if (
      data.allowNegativeBalance &&
      (data.negativeBalanceDays == null || data.negativeBalanceDays <= 0)
    ) {
      ctx.addIssue({
        path: ['negativeBalanceDays'],
        code: 'custom',
        message: t('negativeBalanceDaysRequired'),
      });
    }

    if (
      data.allowNegativeBalance &&
      data.negativeBalanceDays != null &&
      data.negativeBalanceDays > NEGATIVE_BALANCE_DAYS_MAX_VALUE
    ) {
      ctx.addIssue({
        path: ['negativeBalanceDays'],
        code: 'custom',
        message: t('negativeBalanceDaysMax', { max: NEGATIVE_BALANCE_DAYS_MAX_VALUE }),
      });
    }

    if (data.hasMaxLimit && (data.maxLimitDays == null || data.maxLimitDays <= 0)) {
      ctx.addIssue({
        path: ['maxLimitDays'],
        code: 'custom',
        message: t('maxLimitDaysRequired'),
      });
    }

    if (
      data.hasMaxLimit &&
      data.maxLimitDays != null &&
      data.maxLimitDays > MAX_LIMIT_DAYS_MAX_VALUE
    ) {
      ctx.addIssue({
        path: ['maxLimitDays'],
        code: 'custom',
        message: t('maxLimitDaysMax', { max: MAX_LIMIT_DAYS_MAX_VALUE }),
      });
    }
  });

export type TimeOffTypeSetupFormData = z.infer<typeof timeOffTypeSetupSchema>;
